import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Upload, FileText, X, ArrowLeft, ArrowRight } from 'lucide-react';
import { Seo } from '@/components/common/Seo';
import { PageHero } from '@/components/common/PageHero';
import { OphirButton } from '@/components/common/OphirButton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { submitSupplier, uploadFile } from '@/lib/api';
import { useContent } from '@/context/ContentContext';

const labelCls = 'text-[12px] font-semibold uppercase tracking-[0.1em] text-ophir-navy';
const DRAFT_KEY = 'ophir_supplier_draft';

const STEPS = [
  'Company', 'Contact', 'Products & brand', 'Wholesale', 'Documents', 'Channels & territory', 'Comments', 'Consent',
];

const emptyForm = {
  company_name: '', website: '', country: '', province_state: '', business_type: '',
  contact_name: '', contact_email: '', contact_phone: '', contact_role: '',
  product_categories: [], brand_names: '', product_description: '',
  wholesale_details: '', pricing_notes: '', moq: '',
  document_ids: [], documents_meta: [],
  sales_channels: '', territories: [],
  comments: '', consent: false, company_website_hp: '',
};

export default function Suppliers() {
  const { content } = useContent();
  const seo = content?.seo?.suppliers;
  const opts = content?.form_options || {};
  const cats = opts.product_categories || [];
  const territories = opts.territories || [];
  const businessTypes = opts.business_types || [];

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Load draft
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) setForm((f) => ({ ...f, ...JSON.parse(raw) }));
    } catch (e) { /* ignore */ }
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (key, val) => set(key, form[key].includes(val) ? form[key].filter((x) => x !== val) : [...form[key], val]);

  const saveDraft = () => {
    try {
      const { documents_meta, ...rest } = form;
      localStorage.setItem(DRAFT_KEY, JSON.stringify(rest));
      toast.success('Draft saved on this device.');
    } catch (e) { toast.error('Could not save draft.'); }
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!form.company_name.trim()) e.company_name = 'Required';
      if (!form.country.trim()) e.country = 'Required';
    }
    if (s === 1) {
      if (!form.contact_name.trim()) e.contact_name = 'Required';
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact_email)) e.contact_email = 'Enter a valid email';
    }
    if (s === 2) {
      if (form.product_categories.length === 0) e.product_categories = 'Select at least one category';
    }
    if (s === 7) {
      if (!form.consent) e.consent = 'Consent is required to submit';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep(step)) setStep((s) => Math.min(s + 1, STEPS.length - 1)); else toast.error('Please complete the required fields.'); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    for (const file of files) {
      if (file.size > 15 * 1024 * 1024) { toast.error(`${file.name} exceeds 15 MB.`); continue; }
      setUploading(true); setUploadPct(0);
      try {
        const meta = await uploadFile(file, setUploadPct);
        setForm((f) => ({
          ...f,
          document_ids: [...f.document_ids, meta.id],
          documents_meta: [...f.documents_meta, meta],
        }));
        toast.success(`Uploaded ${meta.original_name}`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Failed to upload ${file.name}`);
      } finally { setUploading(false); setUploadPct(0); }
    }
  };

  const removeDoc = (id) => setForm((f) => ({
    ...f,
    document_ids: f.document_ids.filter((x) => x !== id),
    documents_meta: f.documents_meta.filter((m) => m.id !== id),
  }));

  const onSubmit = async () => {
    if (!validateStep(7)) return;
    setSubmitting(true);
    try {
      const { documents_meta, ...payload } = form;
      await submitSupplier(payload);
      localStorage.removeItem(DRAFT_KEY);
      setDone(true); window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <>
        <Seo title={seo?.title} description={seo?.description} />
        <PageHero label="For suppliers" title="Submission received" />
        <section className="bg-white"><div className="container-ophir py-16">
          <div data-testid="supplier-success" className="max-w-2xl bg-ophir-ivory-soft border border-ophir-grey rounded-2xl p-8 shadow-ophir">
            <CheckCircle2 className="text-ophir-navy" size={44} />
            <h2 className="mt-4 font-serif text-[26px] text-ophir-navy-deep">Thank you for introducing your company</h2>
            <p className="mt-4 text-[15.5px] leading-relaxed text-ophir-muted">
              Thank you for introducing your company to OPHIR SUPPLY. Your information has been received for commercial review. Submission does not guarantee account approval, supplier onboarding or a purchase commitment.
            </p>
            <div className="mt-6"><OphirButton to="/">Return Home</OphirButton></div>
          </div>
        </div></section>
      </>
    );
  }

  const pct = Math.round(((step + 1) / STEPS.length) * 100);

  return (
    <>
      <Seo title={seo?.title} description={seo?.description} />
      <PageHero label="For suppliers" title="Become a supplier"
        intro="Introduce your company, product line or wholesale programme. Each opportunity is reviewed responsibly, with clear communication throughout."
        image="/images/ophir/suppliers/review.webp"
        imageAlt="A supplier representative and a purchasing professional reviewing product samples and wholesale information" />

      <section className="bg-white">
        <div className="container-ophir py-14 lg:py-20">
          <div className="max-w-3xl">
            {/* Progress */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-ophir-gold-deep">Step {step + 1} of {STEPS.length}</span>
                <span className="text-[13px] text-ophir-muted">{STEPS[step]}</span>
              </div>
              <Progress data-testid="supplier-form-progress" value={pct} className="h-2" />
              <div className="mt-3 hidden md:flex flex-wrap gap-x-4 gap-y-1">
                {STEPS.map((s, i) => (
                  <span key={s} className={`text-[12px] ${i === step ? 'text-ophir-navy font-semibold' : i < step ? 'text-ophir-gold-deep' : 'text-ophir-grey-med'}`}>{i + 1}. {s}</span>
                ))}
              </div>
            </div>

            <form data-testid="supplier-form" onSubmit={(e) => e.preventDefault()} noValidate className="space-y-6">
              <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={form.company_website_hp} onChange={(e) => set('company_website_hp', e.target.value)} aria-hidden="true" />

              {step === 0 && (
                <div className="space-y-5">
                  <h2 className="font-serif text-[22px] text-ophir-navy-deep">Company information</h2>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label className={labelCls}>Company name *</Label>
                      <Input data-testid="supplier-company-name" value={form.company_name} onChange={(e) => set('company_name', e.target.value)} className="mt-2" />
                      {errors.company_name && <p className="mt-1 text-[13px] text-[#B42318]">{errors.company_name}</p>}
                    </div>
                    <div>
                      <Label className={labelCls}>Website</Label>
                      <Input data-testid="supplier-website" value={form.website} onChange={(e) => set('website', e.target.value)} className="mt-2" placeholder="https://" />
                    </div>
                    <div>
                      <Label className={labelCls}>Country *</Label>
                      <Input data-testid="supplier-country" value={form.country} onChange={(e) => set('country', e.target.value)} className="mt-2" />
                      {errors.country && <p className="mt-1 text-[13px] text-[#B42318]">{errors.country}</p>}
                    </div>
                    <div>
                      <Label className={labelCls}>Province / state</Label>
                      <Input data-testid="supplier-province" value={form.province_state} onChange={(e) => set('province_state', e.target.value)} className="mt-2" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className={labelCls}>Business type</Label>
                      <Select value={form.business_type} onValueChange={(v) => set('business_type', v)}>
                        <SelectTrigger data-testid="supplier-business-type" className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{businessTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-5">
                  <h2 className="font-serif text-[22px] text-ophir-navy-deep">Contact information</h2>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label className={labelCls}>Contact name *</Label>
                      <Input data-testid="supplier-contact-name" value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} className="mt-2" />
                      {errors.contact_name && <p className="mt-1 text-[13px] text-[#B42318]">{errors.contact_name}</p>}
                    </div>
                    <div>
                      <Label className={labelCls}>Role / title</Label>
                      <Input data-testid="supplier-contact-role" value={form.contact_role} onChange={(e) => set('contact_role', e.target.value)} className="mt-2" />
                    </div>
                    <div>
                      <Label className={labelCls}>Business email *</Label>
                      <Input type="email" data-testid="supplier-contact-email" value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} className="mt-2" />
                      {errors.contact_email && <p className="mt-1 text-[13px] text-[#B42318]">{errors.contact_email}</p>}
                    </div>
                    <div>
                      <Label className={labelCls}>Telephone</Label>
                      <Input data-testid="supplier-contact-phone" value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} className="mt-2" />
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <h2 className="font-serif text-[22px] text-ophir-navy-deep">Product & brand information</h2>
                  <div>
                    <Label className={labelCls}>Product categories *</Label>
                    <div className="mt-3 grid sm:grid-cols-2 gap-2">
                      {cats.map((c) => (
                        <label key={c} className="flex items-center gap-3 rounded-lg border border-ophir-grey bg-ophir-ivory-soft px-4 py-3 cursor-pointer">
                          <Checkbox checked={form.product_categories.includes(c)} onCheckedChange={() => toggle('product_categories', c)} />
                          <span className="text-[14.5px] text-ophir-navy">{c}</span>
                        </label>
                      ))}
                    </div>
                    {errors.product_categories && <p className="mt-1 text-[13px] text-[#B42318]">{errors.product_categories}</p>}
                  </div>
                  <div>
                    <Label className={labelCls}>Brand name(s)</Label>
                    <Input data-testid="supplier-brands" value={form.brand_names} onChange={(e) => set('brand_names', e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label className={labelCls}>Product description</Label>
                    <Textarea data-testid="supplier-product-description" rows={4} value={form.product_description} onChange={(e) => set('product_description', e.target.value)} className="mt-2" placeholder="Briefly describe your products and what makes them suitable." />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5">
                  <h2 className="font-serif text-[22px] text-ophir-navy-deep">Wholesale & commercial details</h2>
                  <div>
                    <Label className={labelCls}>Wholesale programme details</Label>
                    <Textarea data-testid="supplier-wholesale" rows={4} value={form.wholesale_details} onChange={(e) => set('wholesale_details', e.target.value)} className="mt-2" placeholder="Wholesale terms, availability, lead times." />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label className={labelCls}>Pricing notes</Label>
                      <Input data-testid="supplier-pricing" value={form.pricing_notes} onChange={(e) => set('pricing_notes', e.target.value)} className="mt-2" />
                    </div>
                    <div>
                      <Label className={labelCls}>Minimum order quantity</Label>
                      <Input data-testid="supplier-moq" value={form.moq} onChange={(e) => set('moq', e.target.value)} className="mt-2" />
                    </div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-5">
                  <h2 className="font-serif text-[22px] text-ophir-navy-deep">Documentation uploads</h2>
                  <p className="text-[14.5px] text-ophir-muted">Optionally attach line sheets, catalogues, certificates or specifications. Accepted: PDF, Word, Excel, CSV, images. Max 15 MB per file.</p>
                  <label className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-ophir-grey bg-ophir-ivory-soft px-6 py-10 cursor-pointer hover:border-ophir-gold transition-colors">
                    <Upload className="text-ophir-navy" size={26} />
                    <span className="text-[14.5px] font-semibold text-ophir-navy">Choose files to upload</span>
                    <span className="text-[13px] text-ophir-muted">or use your file browser</span>
                    <input data-testid="supplier-form-file-upload" type="file" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*" />
                  </label>
                  {uploading && (
                    <div><Progress value={uploadPct} className="h-2" /><p className="mt-1 text-[13px] text-ophir-muted">Uploading\u2026 {uploadPct}%</p></div>
                  )}
                  {form.documents_meta.length > 0 && (
                    <ul className="space-y-2">
                      {form.documents_meta.map((m) => (
                        <li key={m.id} className="flex items-center justify-between rounded-lg border border-ophir-grey bg-white px-4 py-3">
                          <span className="flex items-center gap-3 text-[14px] text-ophir-navy min-w-0">
                            <FileText size={18} className="text-ophir-gold-deep shrink-0" />
                            <span className="truncate">{m.original_name}</span>
                          </span>
                          <button type="button" onClick={() => removeDoc(m.id)} className="text-ophir-muted hover:text-[#B42318]" aria-label={`Remove ${m.original_name}`}><X size={18} /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {step === 5 && (
                <div className="space-y-5">
                  <h2 className="font-serif text-[22px] text-ophir-navy-deep">Sales-channel & territory information</h2>
                  <div>
                    <Label className={labelCls}>Current sales channels</Label>
                    <Textarea data-testid="supplier-channels" rows={3} value={form.sales_channels} onChange={(e) => set('sales_channels', e.target.value)} className="mt-2" placeholder="e.g. retail, e-commerce, distributors, direct." />
                  </div>
                  <div>
                    <Label className={labelCls}>Territories</Label>
                    <div className="mt-3 grid sm:grid-cols-2 gap-2">
                      {territories.map((t) => (
                        <label key={t} className="flex items-center gap-3 rounded-lg border border-ophir-grey bg-ophir-ivory-soft px-4 py-3 cursor-pointer">
                          <Checkbox checked={form.territories.includes(t)} onCheckedChange={() => toggle('territories', t)} />
                          <span className="text-[14.5px] text-ophir-navy">{t}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 6 && (
                <div className="space-y-5">
                  <h2 className="font-serif text-[22px] text-ophir-navy-deep">Additional comments</h2>
                  <div>
                    <Label className={labelCls}>Anything else we should know?</Label>
                    <Textarea data-testid="supplier-comments" rows={6} value={form.comments} onChange={(e) => set('comments', e.target.value)} className="mt-2" />
                  </div>
                </div>
              )}

              {step === 7 && (
                <div className="space-y-5">
                  <h2 className="font-serif text-[22px] text-ophir-navy-deep">Consent & submission</h2>
                  <div className="rounded-xl border border-ophir-grey bg-ophir-ivory-soft p-5 text-[13.5px] leading-relaxed text-ophir-muted">
                    All product opportunities are subject to commercial review, market suitability, supplier approval, documentation, regulatory requirements, inventory availability and agreed purchasing terms.
                  </div>
                  <div className="flex items-start gap-3">
                    <Checkbox id="s-consent" data-testid="supplier-consent" checked={form.consent} onCheckedChange={(v) => set('consent', !!v)} className="mt-1" />
                    <Label htmlFor="s-consent" className="text-[14px] font-normal leading-relaxed text-ophir-muted">I confirm the information provided is accurate and consent to OPHIR SUPPLY storing and using it for commercial review. *</Label>
                  </div>
                  {errors.consent && <p className="text-[13px] text-[#B42318]">{errors.consent}</p>}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t border-ophir-grey">
                <div className="flex gap-3">
                  <OphirButton variant="secondary" data-testid="supplier-form-back-button" onClick={back} disabled={step === 0} className={step === 0 ? 'opacity-40 pointer-events-none' : ''}>
                    <ArrowLeft size={18} /> Back
                  </OphirButton>
                  <button type="button" onClick={saveDraft} className="text-[14px] text-ophir-navy underline underline-offset-4 decoration-ophir-grey-med hover:decoration-ophir-gold">Save draft</button>
                </div>
                {step < STEPS.length - 1 ? (
                  <OphirButton data-testid="supplier-form-next-button" onClick={next}>Continue <ArrowRight size={18} /></OphirButton>
                ) : (
                  <OphirButton variant="gold" data-testid="supplier-form-submit" onClick={onSubmit} disabled={submitting} className="!bg-ophir-navy !text-white hover:!bg-ophir-navy-deep">
                    {submitting ? 'Submitting\u2026' : 'Submit Your Brand'}
                  </OphirButton>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
